// src/pages/pedidos/ModalTempoPreparo.js
import { useEffect, useState } from 'react';
import { FaClock } from 'react-icons/fa';
import ModalContainer from '../../components/modalForm/ModalContainer';
import ModalHeader from '../../components/modalForm/ModalHeader';
import ModalBody from '../../components/modalForm/ModalBody';
import ModalFooter from '../../components/modalForm/ModalFooter';
import Input from '../../components/input/Input';

const TEMPO_PREPARO_ACEITE_OPCOES_BASE = [10, 20, 30, 40, 50, 60, 70, 90, 100, 120];

const ModalTempoPreparo = ({
  isOpen,
  previsaoMinPadrao,
  isConfirmandoTempoPreparoAceite,
  onCancel,
  onConfirm,
  showMessage,
}) => {
  const [isModalHoraPreparoAceiteOpen, setIsModalHoraPreparoAceiteOpen] = useState(false);
  const [tempoPreparoAceiteSel, setTempoPreparoAceiteSel] = useState('');
  const [horaPreparoAceiteSel, setHoraPreparoAceiteSel] = useState('');

  const tempoPreparoAceiteOpcoes = (() => {
    const previsaoPadrao = Number(previsaoMinPadrao);

    if (!previsaoPadrao) {
      return TEMPO_PREPARO_ACEITE_OPCOES_BASE;
    }

    return [
      previsaoPadrao,
      ...TEMPO_PREPARO_ACEITE_OPCOES_BASE.filter((tempo) => tempo !== previsaoPadrao),
    ];
  })();

  const tempoPreparoAceiteOpcaoPadrao = tempoPreparoAceiteOpcoes[0];
  const tempoPreparoAceiteDemaisOpcoes = tempoPreparoAceiteOpcoes.slice(1);

  const formatTempoPreparoAceite = (tempo) => {
    const horas = Math.floor(tempo / 60);
    const minutos = tempo % 60;

    if (horas === 0) {
      return `${tempo} min`;
    }

    if (minutos === 0) {
      return horas === 1 ? '1 hora' : `${horas} horas`;
    }

    return `${horas}h ${minutos} min`;
  };

  const calcularMinutosAteHorario = (horario) => {
    if (!horario) {
      return null;
    }

    const [hora, minuto] = horario.split(':').map(Number);

    if (Number.isNaN(hora) || Number.isNaN(minuto)) {
      return null;
    }

    const agora = new Date();
    const horarioInformado = new Date(agora);

    horarioInformado.setHours(hora, minuto, 0, 0);

    const diferencaMs = horarioInformado.getTime() - agora.getTime();

    if (diferencaMs <= 0) {
      return null;
    }

    return Math.ceil(diferencaMs / 60000);
  };

  useEffect(() => {
    if (!isOpen) {
      setTempoPreparoAceiteSel('');
      setHoraPreparoAceiteSel('');
      setIsModalHoraPreparoAceiteOpen(false);
      return;
    }

    setTempoPreparoAceiteSel(tempoPreparoAceiteOpcaoPadrao ?? '');
    setHoraPreparoAceiteSel('');
    setIsModalHoraPreparoAceiteOpen(false);
  }, [isOpen, tempoPreparoAceiteOpcaoPadrao]);

  const handleOpenHoraPreparoAceiteModal = () => {
    if (isConfirmandoTempoPreparoAceite) {
      return;
    }

    setIsModalHoraPreparoAceiteOpen(true);
  };

  const handleCancelarHoraPreparoAceite = () => {
    if (isConfirmandoTempoPreparoAceite) {
      return;
    }

    setIsModalHoraPreparoAceiteOpen(false);
  };

  const handleConfirmarHoraPreparoAceite = () => {
    const minutosCalculados = calcularMinutosAteHorario(horaPreparoAceiteSel);

    if (!horaPreparoAceiteSel) {
      showMessage('Informe a hora prevista para concluir o pedido.', 'warning');
      return;
    }

    if (!minutosCalculados) {
      showMessage('Informe uma hora maior que o horário atual.', 'warning');
      return;
    }

    setTempoPreparoAceiteSel(minutosCalculados);
    setIsModalHoraPreparoAceiteOpen(false);
  };

  const handleConfirmarTempoPreparoAceite = () => {
    const tempoSelecionado = Number(tempoPreparoAceiteSel);

    if (!tempoSelecionado) {
      showMessage('Selecione o tempo de preparo.', 'warning');
      return;
    }

    onConfirm(tempoSelecionado);
  };

  return (
    <>
      <ModalContainer largura="620px" show={isOpen}>
        <ModalHeader>
          <h2>Tempo médio de conclusão do pedido</h2>
        </ModalHeader>
        <ModalBody>
          <div className="tempo-preparo-aceite-modal">
            <div className="tempo-preparo-aceite-icon">
              <FaClock />
            </div>

            <div className="tempo-preparo-aceite-content">
              <h3>Selecione o tempo que será informado ao cliente</h3>

              {horaPreparoAceiteSel && Number(tempoPreparoAceiteSel) > 0 && (
                <p className="tempo-preparo-aceite-helper">
                  Horário informado: <strong>{horaPreparoAceiteSel}</strong> ({formatTempoPreparoAceite(Number(tempoPreparoAceiteSel))})
                </p>
              )}

              <div className="tempo-preparo-aceite-grid">
                {typeof tempoPreparoAceiteOpcaoPadrao === 'number' && (
                  <button
                    key={tempoPreparoAceiteOpcaoPadrao}
                    type="button"
                    className={`tempo-preparo-aceite-option ${Number(tempoPreparoAceiteSel) === tempoPreparoAceiteOpcaoPadrao && !horaPreparoAceiteSel ? 'selected' : ''}`}
                    onClick={() => {
                      setHoraPreparoAceiteSel('');
                      setTempoPreparoAceiteSel(tempoPreparoAceiteOpcaoPadrao);
                    }}
                    disabled={isConfirmandoTempoPreparoAceite}
                  >
                    {formatTempoPreparoAceite(tempoPreparoAceiteOpcaoPadrao)}
                  </button>
                )}

                <button
                  type="button"
                  className={`tempo-preparo-aceite-option tempo-preparo-aceite-option-custom ${horaPreparoAceiteSel ? 'selected' : ''}`}
                  onClick={handleOpenHoraPreparoAceiteModal}
                  disabled={isConfirmandoTempoPreparoAceite}
                >
                  <span>Informar a Hora</span>
                  <small>{horaPreparoAceiteSel || 'Ex.: 12:00'}</small>
                </button>

                {tempoPreparoAceiteDemaisOpcoes.map((tempo) => (
                  <button
                    key={tempo}
                    type="button"
                    className={`tempo-preparo-aceite-option ${Number(tempoPreparoAceiteSel) === tempo && !horaPreparoAceiteSel ? 'selected' : ''}`}
                    onClick={() => {
                      setHoraPreparoAceiteSel('');
                      setTempoPreparoAceiteSel(tempo);
                    }}
                    disabled={isConfirmandoTempoPreparoAceite}
                  >
                    {formatTempoPreparoAceite(tempo)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={isConfirmandoTempoPreparoAceite}
          >
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={handleConfirmarTempoPreparoAceite}
            disabled={isConfirmandoTempoPreparoAceite}
          >
            {isConfirmandoTempoPreparoAceite ? 'Aceitando...' : 'Aceitar Pedido'}
          </button>
        </ModalFooter>
      </ModalContainer>

      <ModalContainer largura="420px" show={isModalHoraPreparoAceiteOpen}>
        <ModalHeader>
          <h2>Informar Hora de Conclusão</h2>
        </ModalHeader>
        <ModalBody>
          <div className="tempo-preparo-aceite-hora-modal">
            <Input
              label="Hora prevista"
              value={horaPreparoAceiteSel}
              onChange={(e) => setHoraPreparoAceiteSel(e.target.value)}
              type="time"
              size={180}
              autoFocus
            />

            <p className="tempo-preparo-aceite-hora-texto">
              O sistema calculará automaticamente quantos minutos faltam até esse horário.
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            className="btn btn-secondary"
            onClick={handleCancelarHoraPreparoAceite}
            disabled={isConfirmandoTempoPreparoAceite}
          >
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={handleConfirmarHoraPreparoAceite}
            disabled={isConfirmandoTempoPreparoAceite}
          >
            Confirmar Hora
          </button>
        </ModalFooter>
      </ModalContainer>
    </>
  );
};

export default ModalTempoPreparo;
